// ---------------------------------------------------------------------------
// Canal Rappi — lógica pura.
//
// Todo lo que se puede razonar sin red vive acá, para que sea testeable
// (tests/rappi.test.mjs) y para que las edge functions y el front compartan
// las mismas reglas. Espejo en supabase/functions/_shared/rappi.ts: si cambia
// una regla, cambia en los dos lados.
//
// Diseño: PLAN_RAPPI.md (raíz) y desarrollo/plan-rappi.md (proyecto).
// ---------------------------------------------------------------------------

/** Canal con el que entran las ventas (ver crear_venta_canal). */
export const RP_CHANNEL = 'rappi';

export const RETRY_SCHEDULE_MINUTES = [1, 2, 5, 15, 60, 180, 360, 720];

export function nextRetryDelayMinutes(attemptCount) {
  const i = Math.max(0, Math.floor(Number(attemptCount) || 0));
  return i >= RETRY_SCHEDULE_MINUTES.length ? null : RETRY_SCHEDULE_MINUTES[i];
}

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const texto = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s || null;
};
const redondear2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// ---------------------------------------------------------------------------
// Firma del webhook
// ---------------------------------------------------------------------------

/**
 * Header `Rappi-Signature: t=<timestamp>,sign=<hmac hex>`. Devuelve null si
 * falta alguna de las dos partes: sin firma completa no hay nada que validar.
 */
export function parseRappiSignature(header) {
  if (typeof header !== 'string' || !header.trim()) return null;
  const partes = {};
  for (const trozo of header.split(',')) {
    const i = trozo.indexOf('=');
    if (i <= 0) continue;
    partes[trozo.slice(0, i).trim().toLowerCase()] = trozo.slice(i + 1).trim();
  }
  if (!partes.t || !partes.sign) return null;
  return { t: partes.t, sign: partes.sign };
}

async function hmacSha256Hex(secret, mensaje) {
  const enc = new TextEncoder();
  const key = await globalThis.crypto.subtle.importKey(
    'raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'],
  );
  const firma = await globalThis.crypto.subtle.sign('HMAC', key, enc.encode(mensaje));
  return [...new Uint8Array(firma)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function igualesSinTiempo(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/**
 * Valida la firma: HMAC-SHA256 con el secreto del webhook sobre
 * `${t}.${body}` (body = el texto crudo, tal cual llegó). El timestamp puede
 * venir en segundos o en milisegundos; fuera de la tolerancia se rechaza para
 * que un pedido capturado no se pueda reenviar.
 *
 * @returns {Promise<{ok: boolean, motivo?: string}>}
 */
export async function verificarFirmaRappi({ header, body, secret, ahoraMs = Date.now(), toleranciaSeg = 300 }) {
  if (!secret) return { ok: false, motivo: 'sin_secreto' };
  const firma = parseRappiSignature(header);
  if (!firma) return { ok: false, motivo: 'sin_firma' };
  const t = Number(firma.t);
  if (!Number.isFinite(t) || t <= 0) return { ok: false, motivo: 'timestamp' };
  const tMs = t > 1e12 ? t : t * 1000;
  if (Math.abs(ahoraMs - tMs) > toleranciaSeg * 1000) return { ok: false, motivo: 'timestamp' };
  const esperada = await hmacSha256Hex(secret, `${firma.t}.${body ?? ''}`);
  return igualesSinTiempo(esperada, firma.sign.toLowerCase()) ? { ok: true } : { ok: false, motivo: 'firma' };
}

// ---------------------------------------------------------------------------
// Pedido
// ---------------------------------------------------------------------------

function precioDe(item) {
  return num(item?.unit_price_with_discount)
    ?? num(item?.price)
    ?? num(item?.unit_price)
    ?? num(item?.unit_price_without_discount)
    ?? 0;
}

function toppingDe(sub) {
  return {
    sku: texto(sub?.sku),
    rappi_item_id: texto(sub?.id),
    name: texto(sub?.name),
    qty_per_unit: num(sub?.quantity) ?? 1,
    unit_price: precioDe(sub),
  };
}

/**
 * Pedido de Rappi (webhook NEW_ORDER o GET de órdenes) a la forma que usa la
 * base (rp_evaluar_pedido). Tolera la forma con `order_detail` y la plana.
 *
 * Los toppings (subitems) se quedan DENTRO de su producto, con su cantidad
 * POR UNIDAD del producto: así la base decide si descuentan su propio
 * producto (vinculado) o suman su precio a la línea (sin vincular).
 *
 * Envío, service fee y propina no se leen: no son venta del local.
 *
 * @returns {object|null} null si falta el id del pedido o el del local.
 */
export function normalizarPedidoRappi(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw.order_detail && typeof raw.order_detail === 'object' ? raw.order_detail : raw;
  const store = raw.store ?? d.store ?? {};
  const orderId = texto(d.order_id ?? raw.order_id ?? d.id ?? raw.id);
  const storeId = texto(store?.internal_id ?? store?.id ?? raw.store_id ?? d.store_id);
  if (!orderId || !storeId) return null;

  const items = Array.isArray(d.items) ? d.items : [];
  const lines = [];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const tipo = String(item.type ?? '').toLowerCase();
    const subitems = Array.isArray(item.subitems) ? item.subitems : Array.isArray(item.toppings) ? item.toppings : [];
    if (tipo === 'topping') {
      // Un topping suelto en la raíz pertenece al último producto.
      if (lines.length) lines[lines.length - 1].toppings.push(toppingDe(item));
      continue;
    }
    lines.push({
      sku: texto(item.sku),
      rappi_item_id: texto(item.id),
      name: texto(item.name),
      quantity: num(item.quantity) ?? 1,
      unit_price: precioDe(item),
      comments: texto(item.comments),
      toppings: subitems.filter((s) => s && typeof s === 'object').map(toppingDe),
    });
  }

  const totals = d.totals ?? {};
  const customer = raw.customer ?? d.customer ?? {};
  const nombre = [texto(customer.first_name), texto(customer.last_name)].filter(Boolean).join(' ') || null;

  return {
    order_id: orderId,
    store_id: storeId,
    external_store_id: texto(store?.external_id),
    created_at: texto(d.created_at ?? raw.created_at),
    delivery_method: texto(d.delivery_method ?? raw.delivery_method),
    payment_method: texto(d.payment_method ?? raw.payment_method),
    cooking_time: num(d.cooking_time),
    min_cooking_time: num(d.min_cooking_time),
    max_cooking_time: num(d.max_cooking_time),
    customer_name: nombre,
    total_rappi: num(totals.total_products_with_discount) ?? num(totals.total_products) ?? num(d.total_products),
    lines,
  };
}

/** Total de la venta: (producto + toppings) × cantidad, a centavos. */
export function totalPedidoNormalizado(pedido) {
  const lineas = Array.isArray(pedido?.lines) ? pedido.lines : [];
  let total = 0;
  for (const l of lineas) {
    const extras = (Array.isArray(l.toppings) ? l.toppings : [])
      .reduce((acc, t) => acc + (Number(t.qty_per_unit) || 0) * (Number(t.unit_price) || 0), 0);
    total += (Number(l.quantity) || 0) * ((Number(l.unit_price) || 0) + extras);
  }
  return redondear2(total);
}

/**
 * Efectivo con retiro en el local: la plata entra al cajón. Cualquier otro
 * pedido lo cobra Rappi y lo liquida después (no entra al arqueo).
 */
export function esPagoEnLocal(pedido) {
  return String(pedido?.payment_method ?? '').toLowerCase() === 'cash'
    && String(pedido?.delivery_method ?? '').toLowerCase() === 'pickup';
}

/**
 * Minutos de preparación con los que se acepta: el configurado del local,
 * dentro del rango que admite el pedido. Espejo de rp_tiempo_coccion.
 */
export function resolverTiempoCoccion(pedido, configurado) {
  const base = num(configurado) ?? num(pedido?.cooking_time) ?? 15;
  const min = num(pedido?.min_cooking_time) ?? 1;
  const max = num(pedido?.max_cooking_time) ?? 120;
  return Math.max(min, Math.min(max, base));
}

// ---------------------------------------------------------------------------
// Disponibilidad
// ---------------------------------------------------------------------------

/**
 * Qué mandarle a Rappi para un ítem: 'on', 'off' o null (nada).
 * Espejo de rp_availability_targets.
 *
 *  - Un estado manual (prendido/apagado a mano desde Impulza) manda.
 *  - Sin control de stock no se decide nada, salvo devolver prendido lo
 *    que apagó Impulza.
 *  - stock - reservado - colchón <= 0 -> 'off' (si no se mandó ya).
 *  - Hay stock -> 'on' SOLO si el último envío de Impulza fue 'off'. Si el
 *    ítem está apagado en Rappi y Impulza nunca lo apagó, lo apagó el
 *    comercio: no se toca.
 */
export function decidirDisponibilidad({
  stock, reservado = 0, colchon = 0, ultimoEnviado = null, manual = null, controlaStock = true, archivado = false,
}) {
  // Un producto dado de baja no se vende en Rappi (manda sobre lo manual).
  if (archivado) return ultimoEnviado === 'off' ? null : 'off';
  if (manual === 'on' || manual === 'off') return manual === ultimoEnviado ? null : manual;
  // Sin control de stock Impulza no decide, pero devuelve lo que apagó él
  // (ej. se desactivó el envío, o "según stock" en un plato sin stock).
  if (!controlaStock) return ultimoEnviado === 'off' ? 'on' : null;
  const disponible = (Number(stock) || 0) - (Number(reservado) || 0) - Math.max(0, Number(colchon) || 0);
  if (disponible <= 0) return ultimoEnviado === 'off' ? null : 'off';
  return ultimoEnviado === 'off' ? 'on' : null;
}

// ---------------------------------------------------------------------------
// Menú
// ---------------------------------------------------------------------------

const HIJOS = ['children', 'toppings', 'subitems', 'items', 'products', 'categories', 'options'];

/**
 * Menú de Rappi a filas para rp_import_items. Tolera la forma: todo objeto
 * con `sku` es un ítem; lo que cuelga de un producto es topping; lo que
 * cuelga de una categoría (sin sku) conserva el tipo de su padre.
 *
 * @returns {Array<{rappi_sku, rappi_item_id, name, kind, price}>}
 */
export function menuToItemRows(menu) {
  const filas = new Map();
  const visitar = (nodo, kindHeredado) => {
    if (Array.isArray(nodo)) {
      nodo.forEach((n) => visitar(n, kindHeredado));
      return;
    }
    if (!nodo || typeof nodo !== 'object') return;
    const sku = texto(nodo.sku);
    const tipo = String(nodo.type ?? '').toLowerCase();
    let kindHijos = kindHeredado;
    if (sku) {
      const kind = tipo === 'topping' ? 'topping' : tipo === 'product' ? 'product' : kindHeredado;
      const clave = `${kind}:${sku}`;
      if (!filas.has(clave)) {
        filas.set(clave, {
          rappi_sku: sku,
          rappi_item_id: texto(nodo.id),
          name: texto(nodo.name),
          kind,
          price: num(nodo.price),
        });
      }
      kindHijos = 'topping';
    }
    for (const k of HIJOS) {
      if (nodo[k]) visitar(nodo[k], kindHijos);
    }
  };
  visitar(menu, 'product');
  return [...filas.values()];
}

export function partirEnLotes(lista, tamano = 100) {
  const out = [];
  const arr = Array.isArray(lista) ? lista : [];
  for (let i = 0; i < arr.length; i += tamano) out.push(arr.slice(i, i + tamano));
  return out;
}

// ---------------------------------------------------------------------------
// Etiquetas para la pantalla
// ---------------------------------------------------------------------------

export const RP_ORDER_STATUS_LABELS = {
  recibido: 'Esperando aceptar',
  aceptando: 'Aceptando…',
  rechazando: 'Rechazando…',
  aceptado_sin_venta: 'Aceptado, sin venta',
  venta_creada: 'Registrado',
  rechazado: 'Rechazado',
  cancelado: 'Cancelado',
  conflicto: 'Cancelado con venta',
  error: 'Error',
};

export const RP_STORE_STATUS_LABELS = {
  validando: 'Validando con Rappi',
  activo: 'Activo',
  rechazado: 'Rechazado',
  inactivo: 'Pausado',
};

/**
 * Motivos de rechazo: los cancel_type que acepta la API de Rappi. El resto de
 * los casos (local cerrado, demasiados pedidos) se resuelven cerrando el
 * local en Rappi, no rechazando.
 */
export const RP_REJECT_MOTIVOS = [
  { id: 'ITEM_OUT_OF_STOCK', label: 'Falta un producto' },
  { id: 'ITEM_WRONG_PRICE', label: 'Precio equivocado' },
  { id: 'ITEM_NOT_FOUND', label: 'Producto que no existe' },
  { id: 'ORDER_TOTAL_INCORRECT', label: 'Total incorrecto' },
  { id: 'ORDER_MISSING_INFORMATION', label: 'Faltan datos del pedido' },
  { id: 'ORDER_MISSING_ADDRESS_INFORMATION', label: 'Falta la dirección' },
];
